/**
 * 선착순(FCFS) 쿠폰 — 잔여 조회 + 발급 통합 API client.
 */
import { get, post, IRequestOptions } from '@apis/api';
import { CommonResponse } from '@wconcept/wconcept-common/models/common-response';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;

const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'EXTENSION-API-KEY': process.env.NEXT_PUBLIC_EXTENSION_API_KEY || '',
  },
};

/* ============================================================================
 * 1) 잔여 조회 (status)
 * ========================================================================== */

export interface FcfsCouponStatusItem {
  couponCd: string;
  /** 서버가 발급 상태 코드를 줄 때(선택) */
  status?: string;
  remaining?: number;
}

export type FcfsCouponStatusResponse = CommonResponse<FcfsCouponStatusItem[]>;

export async function requestGetFcfsCouponStatus(
  moduleId: number,
  couponCdList: string[],
  optionParams: IRequestOptions = {}
): Promise<FcfsCouponStatusItem[]> {
  const trimmedList = couponCdList
    .map(cd => String(cd).trim())
    .filter(cd => cd !== '');
  if (trimmedList.length === 0) {
    return [];
  }

  const params = new URLSearchParams();
  params.set('couponCds', trimmedList.join(','));

  const { data } = await get<FcfsCouponStatusResponse>({
    url: `/extension/api/fcfs-coupon/v1/${moduleId}/status?${params.toString()}`,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });

  return data ?? [];
}

/* ============================================================================
 * 2) 발급 요청 (issue)
 * ========================================================================== */

/** 백엔드 응답 status (문서·실응답). data.status 가 SUCCESS 로 오는 경우 포함. */
export type FcfsCouponIssueStatus =
  | 'SUCCESS'
  | 'PENDING'
  | 'ALREADY_ISSUED'
  | 'EXHAUSTED'
  | 'NOT_AUTHORIZED'
  | 'NOT_STARTED'
  | 'SUSPENDED'
  | 'INVALID_COUPON'
  | 'NOT_AVAILABLE'
  | 'FAILED'
  | 'SYSTEM_ERROR';

export interface FcfsCouponIssueRequest {
  promotionId: number;
  moduleId: number;
  couponCd: string;
}

export interface FcfsCouponIssueResponse {
  moduleId?: number;
  status: FcfsCouponIssueStatus;
  /** 서버 안내 문구 — SUCCESS 일 때 알림에 우선 사용 */
  message?: string;
}

export async function requestPostFcfsCouponIssue(
  payload: FcfsCouponIssueRequest,
  optionParams: IRequestOptions = {}
): Promise<FcfsCouponIssueResponse> {
  const { data } = await post<CommonResponse<FcfsCouponIssueResponse>>({
    url: `/extension/api/fcfs-coupon/v1/issue`,
    data: payload,
    options: { ...options, ...optionParams },
    baseUrl: baseURL,
  });
  if (data == null) {
    throw new Error('FCFS issue: response data is empty');
  }
  return data;
}
